import React, { useState } from "react";
import { Link } from "react-router-dom";
import MaskedLines from "../common/MaskedLines";
import LiveCover from "../common/LiveCover";
import VrStage from "../projects/VrStage";
import { featuredProject } from "../../data/projects";
import "./FeaturedProject.css";

/**
 * Build the metadata rows for the feature, skipping anything unknown.
 *
 * `location` and `year` are deliberately nullable in the project data — only
 * the projects whose names state a place or a year have them — so the table
 * is assembled rather than hard-coded.
 *
 * @param {import("../../data/projects").Project} project
 * @returns {{ label: string, value: string }[]}
 */
function buildFeatureMetadata(project) {
  const metadataRows = [];

  if (project.location) {
    metadataRows.push({ label: "Lokacija", value: project.location });
  }
  if (project.apartments?.length) {
    metadataRows.push({
      label: "Stanovi",
      value: `${project.apartments.length} tlocrta`,
    });
  }
  if (project.year) {
    metadataRows.push({ label: "Godina", value: String(project.year) });
  }

  return metadataRows;
}

/**
 * Full-bleed feature for the studio's headline project.
 *
 * Two earlier attempts are worth recording so they are not repeated:
 *
 *  1. The project name set enormous with the building's cut-out render
 *     standing in front of it. The render is a wide, opaque object sitting
 *     dead centre, so it covered the middle of the word and left orphaned
 *     letters at the edges — a bug, not a composition.
 *  2. The same cut-out beside the name. The transparent-background PNG
 *     (`mars-otoka-hero-without-bg.png`) is only 618 x 404, so filling half a
 *     desktop column upscaled it nearly 3x on a retina screen and it looked
 *     visibly soft.
 *
 * So the feature now uses the project's own cover render, which is 1429 x 953
 * — roughly a 1:1 match for the column it fills at 2x — presented in a frame
 * like every other photograph on the site.
 *
 * @returns {JSX.Element}
 */
function FeaturedProject() {
  const [isVirtualTourOpen, setIsVirtualTourOpen] = useState(false);
  const project = featuredProject;
  const metadataRows = buildFeatureMetadata(project);

  // "Mars Otoka Sarajevo" -> "Mars": the display word is the name alone.
  const displayWord = project.name.split(" ")[0];

  return (
    <section className="feature section--ink" aria-labelledby="feature-title">
      <div className="shell">
        <div className="section-head__eyebrow feature__eyebrow">
          <span className="t-label t-numeral section-head__index">02</span>
          <span className="t-label">U fokusu</span>
          <span className="t-label feature__eyebrow-aside">
            {project.location}
          </span>
        </div>

        <div className="feature__grid">
          {/* ------------------------------------------------------- name */}
          <div className="feature__head">
            <MaskedLines
              as="h2"
              lines={[displayWord]}
              className="feature__word"
              staggerMs={0}
            />
            <span id="feature-title" className="visually-hidden">
              {project.name}
            </span>
            <p className="feature__typology t-label">{project.typology}</p>
          </div>

          {/* ----------------------------------------------------- render */}
          <Link
            className="feature__media"
            to={`/project/${project.slug}`}
            tabIndex={-1}
            aria-hidden="true"
          >
            <span
              className="frame frame--zoom frame--wide feature__frame"
              data-reveal="wipe"
            >
              <LiveCover
                coverSrc={project.cover}
                alt=""
                preview={project.preview}
                sizes="(min-width: 64rem) 55vw, 100vw"
              />
            </span>
          </Link>

          {/* ---------------------------------------------------- details */}
          <div className="feature__details">
            {metadataRows.length > 0 && (
              <dl className="feature__meta" data-reveal="rise-sm">
                {metadataRows.map((metadataRow) => (
                  <div className="feature__meta-row" key={metadataRow.label}>
                    <dt className="t-label">{metadataRow.label}</dt>
                    <dd className="feature__meta-value">{metadataRow.value}</dd>
                  </div>
                ))}
              </dl>
            )}

            <div className="feature__actions" data-reveal="rise-sm">
              <Link
                className="action action--solid"
                to={`/project/${project.slug}`}
              >
                <span>Pogledaj stanove</span>
              </Link>

              {project.hasVR && (
                <button
                  type="button"
                  className="action action--on-ink"
                  onClick={() => setIsVirtualTourOpen(true)}
                >
                  <span>Virtualna šetnja</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {project.hasVR && (
        <VrStage
          isOpen={isVirtualTourOpen}
          onClose={() => setIsVirtualTourOpen(false)}
          tourUrl={project.vrLink}
          projectName={project.name}
        />
      )}
    </section>
  );
}

export default FeaturedProject;
